<lib-modal
  [closeLabel]="'general.close' | translate"
  [visible]="visible()"
  [title]="'testCases.form.links.modal.title' | translate"
  [contentTemplate]="resolvedTemplate ?? undefined"
  [showButtons]="false"
  [dialogPadding]="'1.25rem'"
  [dialogRadius]="'1rem'"
  (cancel)="onCancel()"
  (visibleChange)="visible.set($event)"
></lib-modal>

<ng-template #content>
  <p class="add-link__subtitle">{{ 'testCases.form.links.modal.subtitle' | translate }}</p>
  <form class="add-link" [formGroup]="form" (ngSubmit)="onSave()">
    <div id="add-link-type-label" class="add-link__typ-label">
      {{ 'testCases.form.links.modal.typeField' | translate }}
    </div>
    <div class="add-link__typen" role="radiogroup" aria-labelledby="add-link-type-label">
      @for (type of linkTypes; track type) {
        @let meta = typeMeta(type);
        @let checked = linkType() === type;
        <button
          type="button"
          role="radio"
          class="add-link__typ"
          [class.add-link__typ--gewaehlt]="checked"
          [attr.data-testid]="typeCardTestId(type)"
          [attr.aria-checked]="checked"
          [tabIndex]="checked ? 0 : -1"
          (click)="onTypeCardClick(type, $event)"
          (keydown)="onTypeCardKeydown($event)"
        >
          <span class="add-link__typ-kachel" aria-hidden="true" [style.--add-link-typfarbe]="meta.color">
            @switch (meta.icon.kind) {
              @case ('app-icon') {
                <app-icon [name]="meta.icon.name" [size]="16" />
              }
              @case ('fa') {
                <i [class]="meta.icon.cls"></i>
              }
              @case ('mat') {
                <mat-icon>{{ meta.icon.name }}</mat-icon>
              }
            }
          </span>
          <span class="add-link__typ-inhalt">
            <span class="add-link__typ-titel">
              {{ 'testCases.form.links.modal.types.' + type + '.label' | translate }}
            </span>
            <span class="add-link__typ-text">
              {{ 'testCases.form.links.modal.types.' + type + '.description' | translate }}
            </span>
          </span>
          <span class="add-link__radio" aria-hidden="true"></span>
        </button>
      }
    </div>

    @if (linkType() === 'REQUIREMENT') {
      <div class="add-link__field">
        <div class="add-link__label">{{ 'testCases.form.links.modal.requirementField' | translate }}</div>
        @if (chosenRequirement(); as chosen) {
          <div class="add-link__anf-gewaehlt">
            <app-icon class="add-link__anf-icon" name="requirement" [size]="16" />
            <code class="add-link__anf-key">{{ chosen.key }}</code>
            <span class="add-link__anf-titel">{{ chosen.title }}</span>
            <a
              class="add-link__anf-oeffnen"
              target="_blank"
              rel="noopener"
              [routerLink]="requirementRoute(chosen)"
              [attr.aria-label]="'testCases.form.links.modal.requirementOpenAria' | translate: { key: chosen.key }"
            >
              {{ 'testCases.form.links.modal.requirementOpen' | translate }}
            </a>
            <span class="add-link__anf-trenner" aria-hidden="true"></span>
            <button type="button" class="add-link__anf-aendern" (click)="openPicker($any($event.currentTarget))">
              {{ 'testCases.form.links.modal.requirementChange' | translate }}
            </button>
          </div>
        } @else {
          <button
            type="button"
            class="add-link__anf-waehlen"
            data-testid="tc-link-requirement-picker"
            [attr.aria-invalid]="referenceErrorKey() ? true : null"
            [attr.aria-describedby]="referenceErrorKey() ? 'add-link-reference-error' : null"
            (click)="openPicker($any($event.currentTarget))"
          >
            <app-icon name="requirement" [size]="15" />
            {{ 'testCases.form.links.modal.requirementPick' | translate }}
          </button>
        }
        @if (referenceErrorKey(); as errorKey) {
          <p id="add-link-reference-error" class="add-link__error" role="alert">
            <mat-icon aria-hidden="true">error</mat-icon>
            <span>{{ errorKey | translate }}</span>
          </p>
        }
      </div>
    } @else {
      <div class="add-link__field">
        <label class="add-link__label" for="add-link-reference">
          {{ 'testCases.form.links.modal.referenceField' | translate }}
          <span class="add-link__required" aria-hidden="true">*</span>
        </label>
        <input
          id="add-link-reference"
          class="add-link__input"
          formControlName="reference"
          required
          [attr.aria-required]="true"
          [attr.maxlength]="referenceMax"
          [attr.aria-invalid]="referenceErrorKey() ? true : null"
          [attr.aria-describedby]="referenceErrorKey() ? 'add-link-reference-error' : 'add-link-reference-hint'"
          [placeholder]="'testCases.form.links.modal.referencePlaceholder.' + linkType() | translate"
        />
        <span id="add-link-reference-hint" class="add-link__hint">
          {{ 'testCases.form.links.modal.referenceHint' | translate }}
        </span>
        @if (referenceErrorKey(); as errorKey) {
          <p id="add-link-reference-error" class="add-link__error" role="alert">
            <mat-icon aria-hidden="true">error</mat-icon>
            <span>{{ errorKey | translate }}</span>
          </p>
        }
      </div>

      <div class="add-link__field">
        <label class="add-link__label" for="add-link-display-label">
          {{ 'testCases.form.links.modal.labelField' | translate }}
          <span class="add-link__optional">{{ 'testCases.form.links.modal.labelOptional' | translate }}</span>
        </label>
        <input
          id="add-link-display-label"
          class="add-link__input"
          formControlName="displayLabel"
          [attr.maxlength]="fieldLengths.NAME_MAX"
          aria-describedby="add-link-display-label-hint"
        />
        <span id="add-link-display-label-hint" class="add-link__hint">
          {{ 'testCases.form.links.modal.labelHint' | translate }}
        </span>
      </div>
    }

    <div class="add-link__fuss">
      <button type="button" class="add-link__abbrechen" (click)="onCancel()">
        {{ 'testCases.form.links.modal.cancel' | translate }}
      </button>
      <button type="submit" class="add-link__verknuepfen" data-testid="tf-verknuepfen" [disabled]="form.invalid">
        <mat-icon aria-hidden="true">add</mat-icon>
        {{ 'testCases.form.links.modal.save' | translate }}
      </button>
    </div>
  </form>

  @if (pickerOpen()) {
    <app-anforderung-auswahl-dialog
      [projectId]="projectId()"
      [excludedRequirementKeys]="excludedRequirementKeys()"
      [value]="chosenRequirement()?.key ?? null"
      (picked)="onRequirementPicked($event)"
      (closed)="closePicker()"
    />
  }
</ng-template>
